<template>
	<div class="ai-testing-ground-wrapper">
		<BackendHeader />
		<div class="ai-testing-container">
			<div class="ai-testing-header">
				<h1>AI Services Testing Ground</h1>
			</div>

			<div class="tabs">
				<button 
					class="tab-button" 
					:class="{ active: activeTab === 'gemini' }"
					@click="activeTab = 'gemini'"
				>
					Google Gemini
				</button>
				<button 
					class="tab-button" 
					:class="{ active: activeTab === 'huggingface' }"
					@click="activeTab = 'huggingface'"
				>
					HuggingFace
				</button>
			</div>

			<!-- Gemini Tab -->
			<div v-if="activeTab === 'gemini'" class="tab-content">
				<div class="form-section">
					<!-- API Key is now hardcoded and hidden -->

					<div class="form-group">
						<label>Model:</label>
						<select v-model="geminiConfig.model" class="select-field">
							<optgroup label="Gemini 3 (Most Intelligent)">
								<option value="gemini-3-pro-preview">Gemini 3 Pro Preview</option>
								<option value="gemini-3-flash-preview">Gemini 3 Flash Preview</option>
								<option value="gemini-3-pro-image-preview">Nano Banana Pro (gemini-3-pro-image-preview) - Professional asset production, advanced reasoning, high-fidelity text</option>
							</optgroup>
							<optgroup label="Gemini 2.5 (Best Price-Performance)">
								<option value="gemini-2.5-flash">Gemini 2.5 Flash</option>
								<option value="gemini-2.5-flash-image">Nano Banana (gemini-2.5-flash-image) - Speed & efficiency, high-volume, low-latency</option>
							</optgroup>
							<optgroup label="Gemini 2.0">
								<option value="gemini-2.0-flash">Gemini 2.0 Flash</option>
								<option value="gemini-2.0-flash-lite">Gemini 2.0 Flash Lite</option>
								<option value="gemini-2.0-flash-thinking-exp">Gemini 2.0 Flash Thinking (Experimental)</option>
								<option value="gemini-2.0-flash-preview-image-generation">Gemini 2.0 Flash Image</option>
							</optgroup>
							<optgroup label="Gemini 1.5">
								<option value="gemini-1.5-pro">Gemini 1.5 Pro</option>
								<option value="gemini-1.5-pro-latest">Gemini 1.5 Pro Latest</option>
								<option value="gemini-1.5-flash">Gemini 1.5 Flash</option>
								<option value="gemini-1.5-flash-latest">Gemini 1.5 Flash Latest</option>
							</optgroup>
							<optgroup label="Legacy Models">
								<option value="gemini-pro">Gemini Pro (Legacy)</option>
								<option value="gemini-pro-vision">Gemini Pro Vision (Legacy)</option>
							</optgroup>
						</select>
					</div>

					<div class="form-group">
						<label>Operation:</label>
						<select v-model="geminiConfig.operation" class="select-field">
							<option value="text">Text Generation (All models)</option>
							<option value="image" :disabled="!isImageGenerationModel">Image Generation (Image models only)</option>
							<option value="chat">Chat (Most models)</option>
							<option value="multimodal" :disabled="!isVisionModel">Multimodal (Vision models only)</option>
						</select>
						<small v-if="geminiConfig.operation === 'image' && !isImageGenerationModel" class="warning-text">
							⚠️ Image generation only works with: gemini-2.5-flash-image, gemini-3-pro-image-preview, gemini-2.0-flash-preview-image-generation
						</small>
						<small v-if="geminiConfig.operation === 'multimodal' && !isVisionModel" class="warning-text">
							⚠️ Multimodal requires a vision-capable model (most Pro/Flash models support this, but not image-only models)
						</small>
					</div>

					<div class="form-group">
						<label>Prompt:</label>
						<textarea 
							v-model="geminiConfig.prompt" 
							placeholder="Enter your prompt here..."
							class="textarea-field"
							rows="5"
						></textarea>
					</div>

					<div v-if="geminiConfig.operation === 'multimodal'" class="form-group">
						<label>Image (Base64):</label>
						<textarea 
							v-model="geminiConfig.imageData" 
							placeholder="Paste base64 encoded image data..."
							class="textarea-field"
							rows="3"
						></textarea>
					</div>

					<div class="form-group">
						<label>
							Temperature:
							<span class="info-icon" title="Controls randomness: 0 = deterministic/focused, 1 = balanced, 2 = creative/random">ℹ️</span>
						</label>
						<input 
							type="number" 
							v-model.number="geminiConfig.temperature" 
							min="0" 
							max="2" 
							step="0.1"
							class="input-field"
						/>
						<small class="help-text">
							Recommended: 0.7-1.0 (balanced), 0.2-0.5 (focused/factual), 1.0-1.5 (creative)
						</small>
					</div>

					<div class="form-group">
						<label>
							Max Output Tokens:
							<span class="info-icon" title="Maximum length of the response. 1 token ≈ 4 characters or 0.75 words">ℹ️</span>
						</label>
						<input 
							type="number" 
							v-model.number="geminiConfig.maxOutputTokens" 
							min="1" 
							max="8192"
							class="input-field"
						/>
						<small class="help-text">
							Recommended: 100-500 (short), 500-2000 (medium), 2000-8192 (long). Default: 1024
						</small>
					</div>

					<div v-if="geminiConfig.operation === 'image'" class="form-group">
						<label>
							Aspect Ratio:
							<span class="info-icon" title="Image aspect ratio">ℹ️</span>
						</label>
						<select v-model="geminiConfig.aspectRatio" class="select-field">
							<option value="">Default</option>
							<option value="1:1">1:1 (Square)</option>
							<option value="3:2">3:2</option>
							<option value="2:3">2:3</option>
							<option value="3:4">3:4</option>
							<option value="4:3">4:3</option>
							<option value="4:5">4:5</option>
							<option value="5:4">5:4</option>
							<option value="9:16">9:16</option>
							<option value="16:9">16:9</option>
							<option value="21:9">21:9</option>
						</select>
						<small class="help-text">
							Select the aspect ratio for the generated image
						</small>
					</div>

					<div v-if="geminiConfig.operation === 'image' && geminiConfig.aspectRatio" class="form-group">
						<label>
							Size:
							<span class="info-icon" title="Image size (shortest dimension)">ℹ️</span>
						</label>
						<select v-model="geminiConfig.imageSize" class="select-field">
							<option value="">Default</option>
							<option value="128">Small (128px)</option>
							<option value="256">Medium (256px)</option>
							<option value="512">Large (512px)</option>
							<option value="1024">XL (1024px)</option>
							<option value="2048">XXL (2048px)</option>
						</select>
						<small class="help-text">
							Select the size for the generated image. Dimensions will be calculated based on aspect ratio.
						</small>
					</div>

					<button 
						class="main-button" 
						@click="testGemini()" 
						:disabled="!canTestGemini || geminiLoading"
					>
						{{ geminiLoading ? 'Testing...' : 'Test Gemini' }}
					</button>
				</div>

				<div v-if="geminiResult" class="result-section">
					<h3>Result:</h3>
					
					<!-- Display generated image if this is an image generation operation -->
					<div v-if="geminiConfig.operation === 'image' && generatedImageUrl" class="image-result-box">
						<h4>Generated Image:</h4>
						<img :src="generatedImageUrl" alt="Generated image" class="generated-image" />
						<div class="image-info">
							<small v-if="isResized">Resized to: {{ geminiConfig.imageSize }}px (shortest dimension)</small>
							<small v-else>Image size: {{ generatedImageSize }}</small>
						</div>
					</div>
					
					<div class="result-box">
						<pre>{{ JSON.stringify(geminiResult, null, 2) }}</pre>
					</div>
				</div>

				<div v-if="geminiError" class="error-section">
					<h3>Error:</h3>
					<div class="error-box">
						{{ geminiError }}
					</div>
				</div>
			</div>

			<!-- HuggingFace Tab -->
			<div v-if="activeTab === 'huggingface'" class="tab-content">
				<div class="form-section">
					<!-- API Token is now hardcoded and hidden -->

					<div class="model-browser-section">
						<div class="model-browser-header">
							<h3>Browse Models</h3>
							<button 
								class="main-button small-button" 
								@click="toggleModelBrowser()"
							>
								{{ showModelBrowser ? 'Hide' : 'Show' }} Model Browser
							</button>
						</div>

						<div v-if="showModelBrowser" class="model-browser-content">
							<div class="form-group">
								<label>Search Models:</label>
								<div class="search-controls">
									<input 
										type="text" 
										v-model="modelSearch.search" 
										placeholder="Search by name..."
										class="input-field"
										@keyup.enter="searchModels()"
									/>
									<input 
										type="text" 
										v-model="modelSearch.filter" 
										placeholder="Filter by tag (e.g., text-generation)"
										class="input-field"
										@keyup.enter="searchModels()"
									/>
									<button 
										class="main-button small-button" 
										@click="searchModels()"
										:disabled="loadingModels"
									>
										{{ loadingModels ? 'Loading...' : 'Search' }}
									</button>
								</div>
							</div>

							<div v-if="models.length > 0" class="models-list">
								<div class="models-header">
									<span>Found {{ models.length }} models</span>
									<button 
										class="link-button" 
										@click="clearModelSearch()"
									>
										Clear
									</button>
								</div>
								<div class="models-grid">
									<div 
										v-for="model in models" 
										:key="model.id"
										class="model-card"
										:class="{ selected: huggingFaceConfig.modelId === model.id }"
										@click="selectModel(model.id)"
									>
										<div class="model-card-header">
											<strong>{{ model.id }}</strong>
											<span v-if="model.gated" class="badge badge-gated">Gated</span>
										</div>
										<div class="model-card-body">
											<div v-if="model.author" class="model-info">
												<small>Author: {{ model.author }}</small>
											</div>
											<div v-if="model.pipelineTag" class="model-info">
												<small>Task: {{ model.pipelineTag }}</small>
											</div>
											<div v-if="model.libraryName" class="model-info">
												<small>Library: {{ model.libraryName }}</small>
											</div>
											<div class="model-info">
												<small>Downloads: {{ formatNumber(model.downloads) }}</small>
											</div>
											<div v-if="model.tags && model.tags.length > 0" class="model-tags">
												<span 
													v-for="tag in model.tags.slice(0, 3)" 
													:key="tag"
													class="tag"
												>
													{{ tag }}
												</span>
												<span v-if="model.tags.length > 3" class="tag-more">
													+{{ model.tags.length - 3 }} more
												</span>
											</div>
										</div>
									</div>
								</div>
							</div>

							<div v-if="models.length === 0 && !loadingModels && modelSearchSearched" class="no-models">
								No models found. Try different search terms.
							</div>
						</div>
					</div>

					<div class="form-group">
						<label>Model ID:</label>
						<select v-model="huggingFaceConfig.modelId" class="select-field" @change="onHuggingFaceModelChange">
							<option value="">Select a model...</option>
							<option value="deepseek-ai/DeepSeek-V3.2:novita">deepseek-ai/DeepSeek-V3.2:novita</option>
							<option value="black-forest-labs/FLUX.1-dev">black-forest-labs/FLUX.1-dev (Recommended)</option>
							<option value="Qwen/Qwen-Image">Qwen/Qwen-Image (Recommended)</option>
							<option value="stabilityai/stable-diffusion-xl-base-1.0">stabilityai/stable-diffusion-xl-base-1.0</option>
							<option value="OTHER">OTHER (Custom Model)</option>
						</select>
						<small class="help-text" style="color: #666; margin-top: 5px; display: block;">
							Note: Some models may not be available through hf-inference. Use the model browser to find available models.
						</small>
						<input 
							v-if="huggingFaceConfig.modelId === 'OTHER'"
							type="text" 
							v-model="huggingFaceConfig.customModelId" 
							placeholder="e.g., gpt2, facebook/bart-large-cnn"
							class="input-field"
							style="margin-top: 10px;"
						/>
						<small class="help-text">
							Or browse and select a model above
						</small>
					</div>

					<div class="form-group">
						<label>Operation:</label>
						<select v-model="huggingFaceConfig.operation" class="select-field" :disabled="!allowedOperations.length">
							<option v-for="op in allowedOperations" :key="op.value" :value="op.value">
								{{ op.label }}
							</option>
						</select>
						<small v-if="!allowedOperations.length" class="warning-text">
							Please select a model first
						</small>
					</div>

					<div v-if="huggingFaceConfig.operation === 'chatcompletions'" class="form-group">
						<label>Message:</label>
						<textarea 
							v-model="huggingFaceConfig.chatMessage" 
							placeholder="Enter your message here..."
							class="textarea-field"
							rows="5"
						></textarea>
						<small class="help-text">
							Your message will be sent as a user message to the chat model
						</small>
					</div>

					<div v-if="huggingFaceConfig.operation !== 'chatcompletions'" class="form-group">
						<label>Inputs:</label>
						<textarea 
							v-model="huggingFaceConfig.inputs" 
							placeholder="Enter input text..."
							class="textarea-field"
							rows="5"
						></textarea>
					</div>

					<div v-if="huggingFaceConfig.operation === 'questionanswering'" class="form-group">
						<label>Question:</label>
						<input 
							type="text" 
							v-model="huggingFaceConfig.question" 
							placeholder="Enter your question"
							class="input-field"
						/>
					</div>

					<div v-if="huggingFaceConfig.operation === 'questionanswering'" class="form-group">
						<label>Context:</label>
						<textarea 
							v-model="huggingFaceConfig.context" 
							placeholder="Enter context for the question"
							class="textarea-field"
							rows="3"
						></textarea>
					</div>

					<div v-if="huggingFaceConfig.operation === 'zeroshot'" class="form-group">
						<label>Candidate Labels (comma-separated):</label>
						<input 
							type="text" 
							v-model="huggingFaceConfig.candidateLabelsText" 
							placeholder="e.g., positive, negative, neutral"
							class="input-field"
						/>
					</div>

					<div v-if="huggingFaceConfig.operation === 'zeroshot'" class="form-group">
						<label>
							<input 
								type="checkbox" 
								v-model="huggingFaceConfig.multiLabel"
							/>
							Multi-label
						</label>
					</div>

					<div v-if="huggingFaceConfig.operation === 'chatcompletions' || huggingFaceConfig.operation === 'textgeneration'" class="form-group">
						<label>
							Temperature:
							<span class="info-icon" title="Controls randomness: 0 = deterministic/focused, 1 = balanced, 2 = creative/random">ℹ️</span>
						</label>
						<input 
							type="number" 
							v-model.number="huggingFaceConfig.temperature" 
							min="0" 
							max="2" 
							step="0.1"
							class="input-field"
						/>
						<small class="help-text">
							Recommended: 0.7-1.0 (balanced), 0.2-0.5 (focused/factual), 1.0-1.5 (creative)
						</small>
					</div>

					<div v-if="huggingFaceConfig.operation === 'chatcompletions'" class="form-group">
						<label>
							Max Tokens:
							<span class="info-icon" title="Maximum tokens to generate. 1 token ≈ 4 characters or 0.75 words">ℹ️</span>
						</label>
						<input 
							type="number" 
							v-model.number="huggingFaceConfig.maxTokens" 
							min="1" 
							max="4096"
							class="input-field"
						/>
						<small class="help-text">
							Recommended: 100-500 (short), 500-2000 (medium), 2000-4096 (long). Default: 1000
						</small>
					</div>

					<div v-if="huggingFaceConfig.operation === 'textgeneration'" class="form-group">
						<label>
							Max New Tokens:
							<span class="info-icon" title="Maximum length of the generated text. 1 token ≈ 4 characters or 0.75 words">ℹ️</span>
						</label>
						<input 
							type="number" 
							v-model.number="huggingFaceConfig.maxNewTokens" 
							min="1" 
							max="1024"
							class="input-field"
						/>
						<small class="help-text">
							Recommended: 50-200 (short), 200-500 (medium), 500-1024 (long). Default: 50
						</small>
					</div>

					<!-- Image Generation Parameters -->
					<div v-if="huggingFaceConfig.operation === 'imagegeneration'" class="form-group">
						<label>
							Guidance Scale:
							<span class="info-icon" title="Higher values make the image more closely linked to the prompt. Recommended: 7-9">ℹ️</span>
						</label>
						<input 
							type="number" 
							v-model.number="huggingFaceConfig.guidanceScale" 
							min="1" 
							max="20" 
							step="0.5"
							class="input-field"
							placeholder="e.g., 7.5"
						/>
						<small class="help-text">
							Recommended: 7-9. Higher values = more prompt adherence, but may cause artifacts
						</small>
					</div>

					<div v-if="huggingFaceConfig.operation === 'imagegeneration'" class="form-group">
						<label>Negative Prompt:</label>
						<textarea 
							v-model="huggingFaceConfig.negativePrompt" 
							placeholder="What NOT to include in the image (e.g., blurry, low quality, distorted)"
							class="textarea-field"
							rows="2"
						></textarea>
						<small class="help-text">
							Describe what you want to avoid in the generated image
						</small>
					</div>

					<div v-if="huggingFaceConfig.operation === 'imagegeneration'" class="form-group">
						<label>
							Num Inference Steps:
							<span class="info-icon" title="More steps = higher quality but slower. Recommended: 20-50">ℹ️</span>
						</label>
						<input 
							type="number" 
							v-model.number="huggingFaceConfig.numInferenceSteps" 
							min="1" 
							max="100"
							class="input-field"
							placeholder="e.g., 30"
						/>
						<small class="help-text">
							Recommended: 20-50. More steps = better quality but slower generation
						</small>
					</div>

					<div v-if="huggingFaceConfig.operation === 'imagegeneration'" class="form-group" style="display: flex; gap: 15px;">
						<div style="flex: 1;">
							<label>
								Width (pixels):
								<span class="info-icon" title="Image width in pixels">ℹ️</span>
							</label>
							<input 
								type="number" 
								v-model.number="huggingFaceConfig.imageWidth" 
								min="64" 
								max="2048"
								step="64"
								class="input-field"
								placeholder="e.g., 512"
							/>
						</div>
						<div style="flex: 1;">
							<label>
								Height (pixels):
								<span class="info-icon" title="Image height in pixels">ℹ️</span>
							</label>
							<input 
								type="number" 
								v-model.number="huggingFaceConfig.imageHeight" 
								min="64" 
								max="2048"
								step="64"
								class="input-field"
								placeholder="e.g., 512"
							/>
						</div>
					</div>

					<div v-if="huggingFaceConfig.operation === 'imagegeneration'" class="form-group">
						<label>
							Scheduler:
							<span class="info-icon" title="Override the default scheduler">ℹ️</span>
						</label>
						<input 
							type="text" 
							v-model="huggingFaceConfig.scheduler" 
							placeholder="e.g., DPMSolverMultistepScheduler (optional)"
							class="input-field"
						/>
						<small class="help-text">
							Optional: Override the default scheduler. Leave empty to use model default.
						</small>
					</div>

					<div v-if="huggingFaceConfig.operation === 'imagegeneration'" class="form-group">
						<label>
							Seed:
							<span class="info-icon" title="Random seed for reproducible results. Leave empty for random">ℹ️</span>
						</label>
						<input 
							type="number" 
							v-model.number="huggingFaceConfig.seed" 
							min="0"
							class="input-field"
							placeholder="Leave empty for random"
						/>
						<small class="help-text">
							Optional: Use the same seed to generate the same image. Leave empty for random results.
						</small>
					</div>

					<button 
						class="main-button" 
						@click="testHuggingFace()" 
						:disabled="!canTestHuggingFace || huggingFaceLoading"
					>
						{{ huggingFaceLoading ? 'Testing...' : 'Test HuggingFace' }}
					</button>
				</div>

				<!-- Display generated image if this is an image generation operation -->
				<div v-if="huggingFaceConfig.operation === 'imagegeneration' && huggingFaceGeneratedImageUrl" class="image-result-box">
					<h4>Generated Image:</h4>
					<img :src="huggingFaceGeneratedImageUrl" alt="Generated image" class="generated-image" />
					<div class="image-info">
						<small>Image generated successfully</small>
					</div>
				</div>

				<div v-if="huggingFaceResult" class="result-section">
					<h3>Result:</h3>
					<div class="result-box">
						<pre>{{ JSON.stringify(huggingFaceResult, null, 2) }}</pre>
					</div>
				</div>

				<div v-if="huggingFaceError" class="error-section">
					<h3>Error:</h3>
					<div class="error-box">
						{{ huggingFaceError }}
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import BackendHeader from '@/components/BackendHeader.vue';
import axios from 'axios';
import '@/assets/backend.css';

const activeTab = ref<'gemini' | 'huggingface'>('gemini');

// Hardcoded API keys
const GEMINI_API_KEY = 'AIzaSyBzJJYZ3IdeG5_IpkHxds3H6XYvBnRLqbg';
const HUGGINGFACE_API_TOKEN = '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';

// Gemini State
const geminiConfig = ref({
	apiKey: GEMINI_API_KEY, // Hardcoded
	model: 'gemini-pro',
	operation: 'text',
	prompt: '',
	imageData: '',
	temperature: 0.7,
	maxOutputTokens: 1024,
	aspectRatio: '',
	imageSize: '' // Size in pixels (shortest dimension)
});

const geminiLoading = ref(false);
const geminiResult = ref<any>(null);
const geminiError = ref<string>('');

// Calculate dimensions based on aspect ratio and size
const calculateImageDimensions = (aspectRatio: string, size: number): { width: number; height: number } => {
	if (!aspectRatio || !size) {
		return { width: 0, height: 0 };
	}
	
	const [widthRatio, heightRatio] = aspectRatio.split(':').map(Number);
	const ratio = widthRatio / heightRatio;
	
	let width: number;
	let height: number;
	
	if (ratio >= 1) {
		// Landscape or square - width is the longer side
		width = size;
		height = Math.round(size / ratio);
	} else {
		// Portrait - height is the longer side
		height = size;
		width = Math.round(size * ratio);
	}
	
	return { width, height };
};

// Resize image using Canvas API
const resizeImage = async (imageUrl: string, targetWidth: number, targetHeight: number): Promise<string> => {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => {
			const canvas = document.createElement('canvas');
			canvas.width = targetWidth;
			canvas.height = targetHeight;
			
			const ctx = canvas.getContext('2d');
			if (!ctx) {
				reject(new Error('Could not get canvas context'));
				return;
			}
			
			// Use high-quality image rendering
			ctx.imageSmoothingEnabled = true;
			ctx.imageSmoothingQuality = 'high';
			
			// Draw and resize the image
			ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
			
			// Convert to base64
			const mimeType = imageUrl.match(/data:([^;]+);/)?.[1] || 'image/png';
			const resizedDataUrl = canvas.toDataURL(mimeType, 0.95);
			resolve(resizedDataUrl);
		};
		img.onerror = () => reject(new Error('Failed to load image'));
		img.src = imageUrl;
	});
};

// Store resized image URL and original image URL
const resizedImageUrl = ref<string | null>(null);
const originalImageUrl = ref<string | null>(null);
const isResized = ref(false);

// Extract image data from Gemini response for image generation
const generatedImageUrl = computed(() => {
	if (!geminiResult.value || geminiConfig.value.operation !== 'image') {
		originalImageUrl.value = null;
		resizedImageUrl.value = null;
		isResized.value = false;
		return null;
	}
	
	try {
		// Check if response has candidates with image data
		const candidates = geminiResult.value?.candidates;
		if (!candidates || candidates.length === 0) {
			originalImageUrl.value = null;
			resizedImageUrl.value = null;
			isResized.value = false;
			return null;
		}
		
		// Look for inlineData in the parts
		const parts = candidates[0]?.content?.parts;
		if (!parts || !Array.isArray(parts)) {
			originalImageUrl.value = null;
			resizedImageUrl.value = null;
			isResized.value = false;
			return null;
		}
		
		// Find the part with inlineData (image)
		for (const part of parts) {
			if (part.inlineData && part.inlineData.data) {
				const mimeType = part.inlineData.mimeType || 'image/png';
				const imageUrl = `data:${mimeType};base64,${part.inlineData.data}`;
				originalImageUrl.value = imageUrl;
				
				// Return resized image if available, otherwise return original
				return resizedImageUrl.value || imageUrl;
			}
		}
		
		originalImageUrl.value = null;
		resizedImageUrl.value = null;
		isResized.value = false;
		return null;
	} catch (error) {
		console.error('Error extracting image:', error);
		originalImageUrl.value = null;
		resizedImageUrl.value = null;
		isResized.value = false;
		return null;
	}
});

// Watch for changes and resize image if needed
watch([() => originalImageUrl.value, () => geminiConfig.value.imageSize, () => geminiConfig.value.aspectRatio], async () => {
	if (!originalImageUrl.value || geminiConfig.value.operation !== 'image') {
		resizedImageUrl.value = null;
		isResized.value = false;
		return;
	}
	
	// If size is specified, resize the image
	if (geminiConfig.value.imageSize && geminiConfig.value.aspectRatio && originalImageUrl.value) {
		const size = parseInt(geminiConfig.value.imageSize);
		const dimensions = calculateImageDimensions(geminiConfig.value.aspectRatio, size);
		
		if (dimensions.width > 0 && dimensions.height > 0) {
			try {
				const resized = await resizeImage(originalImageUrl.value, dimensions.width, dimensions.height);
				resizedImageUrl.value = resized;
				isResized.value = true;
			} catch (error) {
				console.error('Error resizing image:', error);
				// Fall back to original image if resize fails
				resizedImageUrl.value = null;
				isResized.value = false;
			}
		} else {
			resizedImageUrl.value = null;
			isResized.value = false;
		}
	} else {
		resizedImageUrl.value = null;
		isResized.value = false;
	}
}, { immediate: true });

const generatedImageSize = computed(() => {
	if (!generatedImageUrl.value) {
		return '';
	}
	
	try {
		const candidates = geminiResult.value?.candidates;
		if (!candidates || candidates.length === 0) {
			return '';
		}
		
		const parts = candidates[0]?.content?.parts;
		if (!parts || !Array.isArray(parts)) {
			return '';
		}
		
		for (const part of parts) {
			if (part.inlineData && part.inlineData.data) {
				const mimeType = part.inlineData.mimeType || 'image/png';
				// Try to get size from the response if available, otherwise show mime type
				return mimeType;
			}
		}
		
		return '';
	} catch (error) {
		return '';
	}
});

const isImageGenerationModel = computed(() => {
	const imageModels = [
		'gemini-2.5-flash-image',
		'gemini-3-pro-image-preview',
		'gemini-2.0-flash-preview-image-generation'
	];
	return imageModels.includes(geminiConfig.value.model);
});

const isVisionModel = computed(() => {
	// Image-only models typically don't support vision input
	const nonVisionModels = [
		'gemini-2.5-flash-image',
		'gemini-3-pro-image-preview',
		'gemini-2.0-flash-preview-image-generation'
	];
	return !nonVisionModels.includes(geminiConfig.value.model);
});

const canTestGemini = computed(() => {
	if (!geminiConfig.value.model || !geminiConfig.value.prompt) {
		return false;
	}
	
	// Image generation requires an image generation model
	if (geminiConfig.value.operation === 'image' && !isImageGenerationModel.value) {
		return false;
	}
	
	// Multimodal requires vision model and image data
	if (geminiConfig.value.operation === 'multimodal') {
		return isVisionModel.value && geminiConfig.value.imageData;
	}
	
	return true;
});

// HuggingFace State
const huggingFaceConfig = ref({
	apiToken: HUGGINGFACE_API_TOKEN, // Hardcoded
	modelId: '',
	customModelId: '',
	operation: 'textgeneration',
	inputs: '',
	question: '',
	context: '',
	candidateLabelsText: '',
	multiLabel: false,
	temperature: 0.7,
	maxNewTokens: 50,
	chatMessage: '',
	maxTokens: 1000,
	// Image generation parameters
	guidanceScale: null as number | null,
	negativePrompt: '',
	numInferenceSteps: null as number | null,
	imageWidth: null as number | null,
	imageHeight: null as number | null,
	scheduler: '',
	seed: null as number | null
});

// Define model operation restrictions
const modelOperationRestrictions: Record<string, string[]> = {
	'deepseek-ai/DeepSeek-V3.2:novita': ['chatcompletions'],
	'black-forest-labs/FLUX.1-dev': ['imagegeneration'],
	'stabilityai/stable-diffusion-xl-base-1.0': ['imagegeneration'],
	'Qwen/Qwen-Image': ['imagegeneration']
};

// All available operations
const allOperations = [
	{ value: 'textgeneration', label: 'Text Generation' },
	{ value: 'chatcompletions', label: 'Chat Completions (Router API)' },
	{ value: 'classification', label: 'Text Classification' },
	{ value: 'questionanswering', label: 'Question Answering' },
	{ value: 'fillmask', label: 'Fill Mask' },
	{ value: 'tokenclassification', label: 'Token Classification (NER)' },
	{ value: 'summarization', label: 'Summarization' },
	{ value: 'translation', label: 'Translation' },
	{ value: 'zeroshot', label: 'Zero-Shot Classification' },
	{ value: 'imagegeneration', label: 'Image Generation' }
];

// Get allowed operations for the selected model
const allowedOperations = computed(() => {
	const modelId = huggingFaceConfig.value.modelId;
	
	if (!modelId || modelId === 'OTHER') {
		// For OTHER, allow all operations
		return allOperations;
	}
	
	// Check if model has restrictions
	if (modelId in modelOperationRestrictions) {
		const allowedOps = modelOperationRestrictions[modelId];
		return allOperations.filter(op => allowedOps.includes(op.value));
	}
	
	// Default: allow all operations
	return allOperations;
});

// Handle model change - reset operation if current one is not allowed
const onHuggingFaceModelChange = () => {
	const allowed = allowedOperations.value;
	if (allowed.length > 0 && !allowed.some(op => op.value === huggingFaceConfig.value.operation)) {
		huggingFaceConfig.value.operation = allowed[0].value;
	}
	
	// Clear custom model ID if not OTHER
	if (huggingFaceConfig.value.modelId !== 'OTHER') {
		huggingFaceConfig.value.customModelId = '';
	}
};

const huggingFaceLoading = ref(false);
const huggingFaceResult = ref<any>(null);
const huggingFaceError = ref<string>('');

// Extract image data from HuggingFace response for image generation
const huggingFaceGeneratedImageUrl = computed(() => {
	if (!huggingFaceResult.value || huggingFaceConfig.value.operation !== 'imagegeneration') {
		return null;
	}
	
	try {
		// HuggingFace image generation returns base64 image in the response
		// Check if response has image property (base64)
		if (huggingFaceResult.value.image) {
			// If it's already a data URL, return it
			if (huggingFaceResult.value.image.startsWith('data:')) {
				return huggingFaceResult.value.image;
			}
			// Get MIME type from response or default to PNG
			const mimeType = huggingFaceResult.value.mimeType || 'image/png';
			// Otherwise, assume it's base64 and add data URL prefix with correct MIME type
			return `data:${mimeType};base64,${huggingFaceResult.value.image}`;
		}
		
		// If response is a direct base64 string
		if (typeof huggingFaceResult.value === 'string') {
			const base64Str = huggingFaceResult.value.trim();
			if (base64Str.length > 0) {
				// Default to PNG if MIME type not available (response is just a string)
				return `data:image/png;base64,${base64Str}`;
			}
		}
		
		return null;
	} catch (error) {
		console.error('Error extracting HuggingFace image:', error);
		return null;
	}
});

// Model Browser State
const showModelBrowser = ref(false);
const models = ref<any[]>([]);
const loadingModels = ref(false);
const modelSearchSearched = ref(false);
const modelSearch = ref({
	search: '',
	filter: '',
	sort: 'downloads',
	direction: -1,
	limit: 50
});

const canTestHuggingFace = computed(() => {
	// Get the actual model ID (either from dropdown or custom input)
	const actualModelId = huggingFaceConfig.value.modelId === 'OTHER' 
		? huggingFaceConfig.value.customModelId 
		: huggingFaceConfig.value.modelId;
	
	if (!actualModelId || actualModelId.trim() === '') {
		return false;
	}
	
	// Check if operation is allowed for this model
	const allowed = allowedOperations.value;
	if (!allowed.some(op => op.value === huggingFaceConfig.value.operation)) {
		return false;
	}
	
	if (huggingFaceConfig.value.operation === 'chatcompletions') {
		return huggingFaceConfig.value.chatMessage && huggingFaceConfig.value.chatMessage.trim().length > 0;
	}
	
	if (!huggingFaceConfig.value.inputs) {
		return false;
	}
	
	if (huggingFaceConfig.value.operation === 'questionanswering') {
		return huggingFaceConfig.value.question && huggingFaceConfig.value.context;
	}
	
	if (huggingFaceConfig.value.operation === 'zeroshot') {
		return huggingFaceConfig.value.candidateLabelsText;
	}
	
	return true;
});

const testGemini = async () => {
	geminiLoading.value = true;
	geminiResult.value = null;
	geminiError.value = '';

	try {
		const requestData: any = {
			apiKey: geminiConfig.value.apiKey,
			model: geminiConfig.value.model,
			operation: geminiConfig.value.operation,
			prompt: geminiConfig.value.prompt
		};

		if (geminiConfig.value.operation === 'multimodal') {
			requestData.imageData = geminiConfig.value.imageData;
			requestData.imageMimeType = 'image/jpeg';
		}

		// Set up generationConfig
		if (geminiConfig.value.operation === 'image' || geminiConfig.value.temperature !== null && geminiConfig.value.temperature !== undefined) {
			requestData.generationConfig = {};
			
			// For image generation, always set responseModalities
			if (geminiConfig.value.operation === 'image') {
				requestData.generationConfig.responseModalities = ['IMAGE'];
			}
			
			// Add temperature and maxOutputTokens if provided
			if (geminiConfig.value.temperature !== null && geminiConfig.value.temperature !== undefined) {
				requestData.generationConfig.temperature = geminiConfig.value.temperature;
				requestData.generationConfig.maxOutputTokens = geminiConfig.value.maxOutputTokens || undefined;
			}
			
			// Add imageConfig for image generation if aspectRatio is provided
			if (geminiConfig.value.operation === 'image' && geminiConfig.value.aspectRatio) {
				requestData.generationConfig.imageConfig = {};
				if (geminiConfig.value.aspectRatio) {
					requestData.generationConfig.imageConfig.aspectRatio = geminiConfig.value.aspectRatio;
				}
			}
		}

		const response = await axios.post('/op/TestGemini', requestData, {
			headers: {
				'X-ApiKey': 'E4D880D7-2FCD-460A-AE30-C0D4982AE757'
			}
		});
		
		if (response.data.success) {
			geminiResult.value = response.data.data;
		} else {
			geminiError.value = response.data.error || 'Unknown error occurred';
		}
	} catch (error: any) {
		geminiError.value = error.response?.data?.message || error.message || 'An error occurred';
		console.error('Gemini test error:', error);
	} finally {
		geminiLoading.value = false;
	}
};

const testHuggingFace = async () => {
	huggingFaceLoading.value = true;
	huggingFaceResult.value = null;
	huggingFaceError.value = '';

	try {
		// Get the actual model ID (either from dropdown or custom input)
		const actualModelId = huggingFaceConfig.value.modelId === 'OTHER' 
			? huggingFaceConfig.value.customModelId 
			: huggingFaceConfig.value.modelId;
		
		const requestData: any = {
			apiToken: huggingFaceConfig.value.apiToken,
			modelId: actualModelId,
			operation: huggingFaceConfig.value.operation
		};

		if (huggingFaceConfig.value.operation === 'chatcompletions') {
			// Construct chat messages array from user input
			requestData.chatMessages = [
				{
					role: 'user',
					content: huggingFaceConfig.value.chatMessage
				}
			];
			requestData.temperature = huggingFaceConfig.value.temperature || undefined;
			requestData.maxTokens = huggingFaceConfig.value.maxTokens || undefined;
		} else {
			requestData.inputs = huggingFaceConfig.value.inputs;
		}

		if (huggingFaceConfig.value.operation === 'questionanswering') {
			requestData.question = huggingFaceConfig.value.question;
			requestData.context = huggingFaceConfig.value.context;
		}

		if (huggingFaceConfig.value.operation === 'zeroshot') {
			requestData.candidateLabels = huggingFaceConfig.value.candidateLabelsText
				.split(',')
				.map((label: string) => label.trim())
				.filter((label: string) => label.length > 0);
			requestData.multiLabel = huggingFaceConfig.value.multiLabel;
		}

		if (huggingFaceConfig.value.operation === 'textgeneration') {
			requestData.textGenerationParameters = {
				temperature: huggingFaceConfig.value.temperature || undefined,
				maxNewTokens: huggingFaceConfig.value.maxNewTokens || undefined
			};
		}

		if (huggingFaceConfig.value.operation === 'imagegeneration') {
			// Add image generation parameters
			const imageParams: any = {};
			if (huggingFaceConfig.value.guidanceScale !== null && huggingFaceConfig.value.guidanceScale !== undefined) {
				imageParams.guidanceScale = huggingFaceConfig.value.guidanceScale;
			}
			if (huggingFaceConfig.value.negativePrompt) {
				imageParams.negativePrompt = huggingFaceConfig.value.negativePrompt;
			}
			if (huggingFaceConfig.value.numInferenceSteps !== null && huggingFaceConfig.value.numInferenceSteps !== undefined) {
				imageParams.numInferenceSteps = huggingFaceConfig.value.numInferenceSteps;
			}
			if (huggingFaceConfig.value.imageWidth !== null && huggingFaceConfig.value.imageWidth !== undefined) {
				imageParams.width = huggingFaceConfig.value.imageWidth;
			}
			if (huggingFaceConfig.value.imageHeight !== null && huggingFaceConfig.value.imageHeight !== undefined) {
				imageParams.height = huggingFaceConfig.value.imageHeight;
			}
			if (huggingFaceConfig.value.scheduler) {
				imageParams.scheduler = huggingFaceConfig.value.scheduler;
			}
			if (huggingFaceConfig.value.seed !== null && huggingFaceConfig.value.seed !== undefined) {
				imageParams.seed = huggingFaceConfig.value.seed;
			}
			
			if (Object.keys(imageParams).length > 0) {
				requestData.imageGenerationParameters = imageParams;
			}
		}

		const response = await axios.post('/op/TestHuggingFace', requestData, {
			headers: {
				'X-ApiKey': 'E4D880D7-2FCD-460A-AE30-C0D4982AE757'
			}
		});
		
		if (response.data.success) {
			huggingFaceResult.value = response.data.data;
		} else {
			huggingFaceError.value = response.data.error || 'Unknown error occurred';
		}
	} catch (error: any) {
		huggingFaceError.value = error.response?.data?.message || error.message || 'An error occurred';
		console.error('HuggingFace test error:', error);
	} finally {
		huggingFaceLoading.value = false;
	}
};

const toggleModelBrowser = () => {
	showModelBrowser.value = !showModelBrowser.value;
	if (showModelBrowser.value && models.value.length === 0 && !modelSearchSearched.value) {
		// Auto-load popular models when opening browser
		searchModels();
	}
};

const searchModels = async () => {
	loadingModels.value = true;
	models.value = [];
	modelSearchSearched.value = true;

	try {
		const requestData: any = {
			apiToken: huggingFaceConfig.value.apiToken,
			sort: modelSearch.value.sort,
			direction: modelSearch.value.direction,
			limit: modelSearch.value.limit
		};

		if (modelSearch.value.search) {
			requestData.search = modelSearch.value.search;
		}

		if (modelSearch.value.filter) {
			requestData.filter = modelSearch.value.filter;
		}

		const response = await axios.post('/op/ListHuggingFaceModels', requestData, {
			headers: {
				'X-ApiKey': 'E4D880D7-2FCD-460A-AE30-C0D4982AE757'
			}
		});

		if (response.data.success) {
			models.value = response.data.data || [];
		} else {
			console.error('Failed to load models:', response.data.error);
			models.value = [];
		}
	} catch (error: any) {
		console.error('Error loading models:', error);
		models.value = [];
	} finally {
		loadingModels.value = false;
	}
};

const selectModel = (modelId: string) => {
	huggingFaceConfig.value.modelId = modelId;
	// Scroll to model ID input
	setTimeout(() => {
		const modelInput = document.querySelector('input[placeholder*="gpt2"]') as HTMLElement;
		if (modelInput) {
			modelInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
			modelInput.focus();
		}
	}, 100);
};

const clearModelSearch = () => {
	modelSearch.value.search = '';
	modelSearch.value.filter = '';
	models.value = [];
	modelSearchSearched.value = false;
};

const formatNumber = (num: number): string => {
	if (num >= 1000000) {
		return (num / 1000000).toFixed(1) + 'M';
	} else if (num >= 1000) {
		return (num / 1000).toFixed(1) + 'K';
	}
	return num.toString();
};
</script>

<style scoped>
.ai-testing-ground-wrapper {
	padding: 20px;
	max-width: 1400px;
	margin: 0 auto;
}

.ai-testing-container {
	background: white;
	border-radius: 8px;
	padding: 20px;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.ai-testing-header {
	margin-bottom: 20px;
}

.ai-testing-header h1 {
	margin: 0;
	color: #333;
}

.tabs {
	display: flex;
	gap: 10px;
	margin-bottom: 20px;
	border-bottom: 2px solid #eee;
}

.tab-button {
	padding: 10px 20px;
	background: none;
	border: none;
	border-bottom: 2px solid transparent;
	cursor: pointer;
	font-size: 16px;
	color: #666;
	transition: all 0.3s;
}

.tab-button:hover {
	color: #333;
}

.tab-button.active {
	color: #007bff;
	border-bottom-color: #007bff;
}

.tab-content {
	margin-top: 20px;
}

.form-section {
	display: flex;
	flex-direction: column;
	gap: 15px;
}

.form-group {
	display: flex;
	flex-direction: column;
	gap: 5px;
}

.form-group label {
	font-weight: 600;
	color: #333;
}

.input-field,
.select-field,
.textarea-field {
	padding: 8px 12px;
	border: 1px solid #ddd;
	border-radius: 4px;
	font-size: 14px;
	font-family: inherit;
}

.textarea-field {
	resize: vertical;
	min-height: 100px;
}

.main-button {
	padding: 12px 24px;
	background: #007bff;
	color: white;
	border: none;
	border-radius: 4px;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.3s;
}

.main-button:hover:not(:disabled) {
	background: #0056b3;
}

.main-button:disabled {
	background: #ccc;
	cursor: not-allowed;
}

.result-section,
.error-section {
	margin-top: 30px;
}

.result-section h3,
.error-section h3 {
	margin-bottom: 10px;
	color: #333;
}

.image-result-box {
	background: #f8f9fa;
	border: 2px solid #007bff;
	border-radius: 8px;
	padding: 20px;
	margin-bottom: 20px;
	text-align: center;
}

.image-result-box h4 {
	margin: 0 0 15px 0;
	color: #333;
	font-size: 16px;
}

.generated-image {
	max-width: 100%;
	max-height: 600px;
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	margin-bottom: 10px;
}

.image-info {
	margin-top: 10px;
	color: #666;
	font-size: 12px;
}

.image-result-box {
	background: #f8f9fa;
	border: 2px solid #007bff;
	border-radius: 8px;
	padding: 20px;
	margin-bottom: 20px;
	text-align: center;
}

.image-result-box h4 {
	margin: 0 0 15px 0;
	color: #333;
	font-size: 16px;
}

.generated-image {
	max-width: 100%;
	max-height: 600px;
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
	margin-bottom: 10px;
}

.image-info {
	margin-top: 10px;
	color: #666;
	font-size: 12px;
}

.result-box,
.error-box {
	padding: 15px;
	border-radius: 4px;
	overflow-x: auto;
}

.result-box {
	background: #f8f9fa;
	border: 1px solid #dee2e6;
}

.error-box {
	background: #f8d7da;
	border: 1px solid #f5c6cb;
	color: #721c24;
}

.result-box pre {
	margin: 0;
	font-size: 12px;
	white-space: pre-wrap;
	word-wrap: break-word;
}

.warning-text {
	color: #dc3545;
	font-size: 12px;
	margin-top: 5px;
	display: block;
}

.select-field option:disabled {
	color: #999;
	background-color: #f5f5f5;
}

.info-icon {
	cursor: help;
	margin-left: 5px;
	font-size: 14px;
	color: #007bff;
}

.help-text {
	color: #666;
	font-size: 12px;
	margin-top: 3px;
	font-style: italic;
}

.model-browser-section {
	margin: 20px 0;
	padding: 15px;
	background: #f8f9fa;
	border-radius: 8px;
	border: 1px solid #dee2e6;
}

.model-browser-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
}

.model-browser-header h3 {
	margin: 0;
	font-size: 18px;
	color: #333;
}

.small-button {
	padding: 6px 12px;
	font-size: 14px;
}

.model-browser-content {
	margin-top: 15px;
}

.search-controls {
	display: flex;
	gap: 10px;
	flex-wrap: wrap;
}

.search-controls .input-field {
	flex: 1;
	min-width: 200px;
}

.models-list {
	margin-top: 20px;
}

.models-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 15px;
	font-weight: 600;
	color: #333;
}

.link-button {
	background: none;
	border: none;
	color: #007bff;
	cursor: pointer;
	text-decoration: underline;
	font-size: 14px;
	padding: 0;
}

.link-button:hover {
	color: #0056b3;
}

.models-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	gap: 15px;
	max-height: 500px;
	overflow-y: auto;
	padding: 5px;
}

.model-card {
	background: white;
	border: 2px solid #dee2e6;
	border-radius: 6px;
	padding: 12px;
	cursor: pointer;
	transition: all 0.2s;
}

.model-card:hover {
	border-color: #007bff;
	box-shadow: 0 2px 8px rgba(0, 123, 255, 0.2);
	transform: translateY(-2px);
}

.model-card.selected {
	border-color: #007bff;
	background: #e7f3ff;
}

.model-card-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 8px;
}

.model-card-header strong {
	font-size: 14px;
	color: #333;
	word-break: break-word;
}

.badge {
	padding: 2px 6px;
	border-radius: 3px;
	font-size: 10px;
	font-weight: 600;
	text-transform: uppercase;
}

.badge-gated {
	background: #ffc107;
	color: #000;
}

.model-card-body {
	font-size: 12px;
}

.model-info {
	margin: 4px 0;
	color: #666;
}

.model-info small {
	display: block;
}

.model-tags {
	display: flex;
	flex-wrap: wrap;
	gap: 4px;
	margin-top: 8px;
}

.tag {
	background: #e9ecef;
	color: #495057;
	padding: 2px 6px;
	border-radius: 3px;
	font-size: 10px;
}

.tag-more {
	color: #6c757d;
	font-size: 10px;
	font-style: italic;
}

.no-models {
	text-align: center;
	padding: 40px;
	color: #6c757d;
	font-style: italic;
}
</style>

